/* Attività on-chain del wallet su UNA catena.
 *
 * Due cose che il prototipo non poteva sapere e che qui contano:
 *
 * 1. `partial: true` significa elenco incompleto PER COSTRUZIONE — la finestra
 *    di blocchi che l'RPC accetta non copre tutta la storia. Senza dirlo,
 *    "nessuna transazione" si legge come "non è successo niente", che è
 *    esattamente la conclusione sbagliata.
 * 2. `chainId` è obbligatorio: non esiste un elenco "su tutte le catene". La
 *    catena mostrata va scritta accanto al titolo, non lasciata implicita.
 *
 * Entrata e uscita non sono verde e rosso: un prelievo non è una perdita.
 * La direzione è scritta a parole e disegnata dalla freccia.
 */

import { Link } from 'react-router-dom';
import { Avvertenza, Distintivo, Pulsante } from '../../componenti';
import { ContenitoreDati, ScheletroLista } from '../../componenti/Stati';
import { dataOra } from '../../lib/formato';
import { importoToken } from './dati';
import type { StatoDati } from '../../api/usaRichiesta';
import type { AttivitaWallet, TransazioneWallet } from '../../api/tipi';

export function Transazioni({
  attivita,
  stato,
  nomeCatena,
  messaggioErrore,
  onRiprova,
}: {
  attivita: AttivitaWallet | null;
  stato: StatoDati;
  nomeCatena: string;
  messaggioErrore?: string;
  onRiprova?: () => void;
}) {
  return (
    <section className="sb-scheda" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: 'var(--sb-s-7) var(--sb-s-7) var(--sb-s-5)' }}>
        <div style={{ fontWeight: 'var(--sb-peso-semi)', fontSize: 'var(--sb-t-14)' }}>
          Transazioni
        </div>
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
          {nomeCatena}
          {attivita?.blocksScanned != null &&
            ` · ultimi ${attivita.blocksScanned} blocchi${
              attivita.approxHours != null ? ` (circa ${attivita.approxHours} ore)` : ''
            }`}
          {attivita?.source && ` · fonte ${attivita.source === 'rpc' ? 'nodo RPC' : 'esploratore'}`}
        </div>
      </div>

      {attivita?.partial && (
        <div style={{ padding: '0 var(--sb-s-7) var(--sb-s-6)' }}>
          <Avvertenza livello="cautela">
            Elenco incompleto per costruzione: la finestra dell'RPC copre solo gli ultimi{' '}
            {attivita.blocksScanned ?? '?'} blocchi. Le operazioni più vecchie esistono ma non sono
            qui.
          </Avvertenza>
        </div>
      )}

      {attivita?.error && (
        <div style={{ padding: '0 var(--sb-s-7) var(--sb-s-6)' }}>
          <Avvertenza livello="cautela">{attivita.error}</Avvertenza>
        </div>
      )}

      <div style={{ padding: stato === 'caricamento' ? 'var(--sb-s-7)' : 0 }}>
        <ContenitoreDati
          stato={stato}
          causaVuoto={`Nessuna transazione su ${nomeCatena} nella finestra di blocchi letta. Non significa che il wallet non abbia storia: significa che non ce n'è in questa finestra.`}
          messaggioErrore={messaggioErrore}
          onRiprova={onRiprova}
          scheletro={<ScheletroLista righe={3} />}
        >
          {(attivita?.transactions ?? []).map((t) => (
            <RigaTransazione key={t.hash} tx={t} esploratore={attivita?.explorerUrl ?? ''} />
          ))}
        </ContenitoreDati>
      </div>
    </section>
  );
}

const VERSO: Record<TransazioneWallet['direction'], string> = {
  in: 'in entrata',
  out: 'in uscita',
  self: 'interna',
  none: 'senza trasferimenti',
};

function RigaTransazione({ tx, esploratore }: { tx: TransazioneWallet; esploratore: string }) {
  /* Una risposta con `ok:false` può non portare gli array: qui si legge, non
     si assume. Un `undefined.length` farebbe cadere l'intera schermata. */
  const trasferimenti = (tx.direction === 'out' ? tx.transfersOut : tx.transfersIn) ?? [];
  const primo = trasferimenti[0];
  const link = esploratore ? `${esploratore.replace(/\/$/, '')}/tx/${tx.hash}` : null;

  return (
    <div className="sb-w-tx">
      <span className="sb-w-tx__medaglione" aria-hidden="true">
        <FrecciaVerso verso={tx.direction} />
      </span>

      <div className="sb-w-tx__corpo">
        <div className="sb-w-tx__titolo">{tx.summary || tx.type}</div>
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
          {VERSO[tx.direction]}
          {tx.timestamp ? ` · ${dataOra(tx.timestamp)}` : ` · blocco ${tx.blockNumber}`}
          {tx.gasFee ? ` · gas ${importoToken(tx.gasFee)} ${tx.nativeSymbol}` : ''}
        </div>

        <div className="sb-riga" style={{ gap: 'var(--sb-s-3)', marginTop: 'var(--sb-s-3)' }}>
          {/* Il ponte fra la catena e il bot: senza, una transazione non si sa
              se l'ha fatta un bot o una mano. */}
          {tx.bot && (
            <Link to={`/tradebot/${tx.bot.tradebotId}`} onClick={(e) => e.stopPropagation()}>
              <Distintivo tono="neutro">
                bot #{tx.bot.tradebotId} · {tx.bot.side === 'BUY' ? 'acquisto' : 'vendita'}
                {tx.bot.asset ? ` ${tx.bot.asset}` : ''}
              </Distintivo>
            </Link>
          )}
          {tx.approvals?.some((a) => a.unlimited) && (
            <Distintivo tono="cautela">approvazione illimitata</Distintivo>
          )}
          {link && (
            <a href={link} target="_blank" rel="noreferrer noopener" className="sb-nota" style={{ color: 'var(--sb-accento-chiaro)' }}>
              vedi sulla catena
            </a>
          )}
        </div>
      </div>

      <span className="sb-w-token__valori">
        <span className="sb-w-token__quantita">
          {primo ? `${importoToken(primo.amount)} ${primo.symbol}` : '—'}
        </span>
        <span className="sb-w-token__usd">
          {trasferimenti.length > 1 ? `e altri ${trasferimenti.length - 1}` : tx.type.toLowerCase()}
        </span>
      </span>
    </div>
  );
}

function FrecciaVerso({ verso }: { verso: TransazioneWallet['direction'] }) {
  const rotazione = verso === 'in' ? 'rotate(180deg)' : verso === 'self' ? 'rotate(90deg)' : 'none';
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: rotazione }}
    >
      <path d="M4 12h16M14 6l6 6-6 6" />
    </svg>
  );
}

/** Pulsante di ricarica dell'elenco: la finestra di blocchi scorre, e chi
 *  guarda dopo un'operazione vuole vederla comparire senza ricaricare tutto. */
export function RicaricaTransazioni({ onRicarica, inCorso }: { onRicarica: () => void; inCorso: boolean }) {
  return (
    <Pulsante variante="tenue" onClick={onRicarica} disabled={inCorso}>
      {inCorso ? 'Lettura in corso…' : 'Rileggi le transazioni'}
    </Pulsante>
  );
}
